"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Trade = void 0;
const currency_1 = require("@sushiswap/currency");
const math_1 = require("@sushiswap/math");
const tiny_invariant_1 = __importDefault(require("tiny-invariant"));
const TradeType_1 = require("./TradeType");
const Version_1 = require("./Version");
/**
 * Represents a trade executed against a list of pools.
 * Does not account for slippage, i.e. trades that front run this trade and move the price.
 */
class Trade {
    /**
     * The route of the trade, i.e. which pools the trade goes through and the input/output currencies.
     */
    route;
    /**
     * The type of the trade, either exact in or exact out.
     */
    tradeType;
    /**
     * The version of the trade, either v1 or v2.
     */
    tradeVersion;
    /**
     * The input amount for the trade assuming no slippage.
     */
    inputAmount;
    /**
     * The output amount for the trade assuming no slippage.
     */
    outputAmount;
    /**
     * The input share for the trade assuming no slippage.
     */
    // public readonly inputShare: Share<TInput>
    /**
     * The output share for the trade assuming no slippage.
     */
    // public readonly outputShare: Share<TOutput>
    /**
     * The price expressed in terms of output amount/input amount.
     */
    executionPrice;
    /**
     * The percent difference between the mid price before the trade and the trade execution price.
     */
    priceImpact = new math_1.Percent(math_1.JSBI.BigInt(0), math_1.JSBI.BigInt(10000));
    currencyInRebase = {
        base: math_1.JSBI.BigInt(0),
        elastic: math_1.JSBI.BigInt(0),
    };
    currencyOutRebase = {
        base: math_1.JSBI.BigInt(0),
        elastic: math_1.JSBI.BigInt(0),
    };
    /**
     * Constructs an exact in trade with the given amount in and route
     * @param route route of the exact in trade
     * @param amountIn the amount being passed in
     * @param currencyOut the output currency
     */
    static exactIn(route, amountIn, currencyOut, version, currencyInRebase = { base: math_1.JSBI.BigInt(0), elastic: math_1.JSBI.BigInt(0) }, currencyOutRebase = { base: math_1.JSBI.BigInt(0), elastic: math_1.JSBI.BigInt(0) }) {
        return new Trade({
            ...route,
            fromToken: amountIn.currency,
            toToken: currencyOut,
        }, TradeType_1.TradeType.EXACT_INPUT, version, currencyInRebase, currencyOutRebase);
    }
    /**
     * Constructs an exact out trade with the given amount out and route
     * @param route route of the exact out trade
     * @param amountOut the amount returned by the trade
     * @param currencyIn the output currency
     */
    static exactOut(route, currencyIn, amountOut, version, currencyInRebase = { base: math_1.JSBI.BigInt(0), elastic: math_1.JSBI.BigInt(0) }, currencyOutRebase = { base: math_1.JSBI.BigInt(0), elastic: math_1.JSBI.BigInt(0) }) {
        return new Trade({
            ...route,
            fromToken: currencyIn,
            toToken: amountOut.currency,
        }, TradeType_1.TradeType.EXACT_OUTPUT, version, currencyInRebase, currencyOutRebase);
    }
    constructor(route, tradeType, tradeVersion, currencyInRebase = { base: math_1.JSBI.BigInt(0), elastic: math_1.JSBI.BigInt(0) }, currencyOutRebase = { base: math_1.JSBI.BigInt(0), elastic: math_1.JSBI.BigInt(0) }) {
        this.route = route;
        this.tradeType = tradeType;
        this.tradeVersion = tradeVersion;
        this.currencyInRebase = currencyInRebase;
        this.currencyOutRebase = currencyOutRebase;
        const amountIn = currency_1.Amount.fromShare(route.fromToken, route.amountInBN.toString(), currencyInRebase, tradeVersion === Version_1.Version.V2);
        const amountOut = currency_1.Amount.fromShare(route.toToken, route.amountOutBN.toString(), currencyOutRebase, tradeVersion === Version_1.Version.V2);
        if (tradeType === TradeType_1.TradeType.EXACT_INPUT) {
            this.inputAmount = currency_1.Amount.fromFractionalAmount(amountIn.currency, amountIn.numerator, amountIn.denominator);
            this.outputAmount = currency_1.Amount.fromFractionalAmount(amountOut.currency, amountOut.numerator, amountOut.denominator);
        }
        else {
            this.inputAmount = currency_1.Amount.fromFractionalAmount(amountIn.currency, amountOut.numerator, amountOut.denominator);
            this.outputAmount = currency_1.Amount.fromFractionalAmount(amountOut.currency, amountIn.numerator, amountIn.denominator);
        }
        this.executionPrice = new currency_1.Price(this.inputAmount.currency, this.outputAmount.currency, this.inputAmount.quotient, this.outputAmount.quotient);
        // Shouldn't really have to check this, but tines makes us
        if (this.route.priceImpact) {
            this.priceImpact = new math_1.Percent(math_1.JSBI.BigInt(Math.round(this.route.priceImpact * 10000)), math_1.JSBI.BigInt(10000));
        }
    }
    /**
     * Get the minimum amount that must be received from this trade for the given slippage tolerance
     * @param slippageTolerance tolerance of unfavorable slippage from the execution price of this trade
     */
    minimumAmountOut(slippageTolerance) {
        (0, tiny_invariant_1.default)(!slippageTolerance.lessThan(math_1.ZERO), 'SLIPPAGE_TOLERANCE');
        if (this.tradeType === TradeType_1.TradeType.EXACT_OUTPUT) {
            return this.outputAmount;
        }
        else {
            const slippageAdjustedAmountOut = new math_1.Fraction(math_1.ONE)
                .add(slippageTolerance)
                .invert()
                .multiply(this.outputAmount.quotient).quotient;
            return currency_1.Amount.fromRawAmount(this.outputAmount.currency, slippageAdjustedAmountOut);
        }
    }
    /**
     * Get the maximum amount in that can be spent via this trade for the given slippage tolerance
     * @param slippageTolerance tolerance of unfavorable slippage from the execution price of this trade
     */
    maximumAmountIn(slippageTolerance) {
        (0, tiny_invariant_1.default)(!slippageTolerance.lessThan(math_1.ZERO), 'SLIPPAGE_TOLERANCE');
        if (this.tradeType === TradeType_1.TradeType.EXACT_INPUT) {
            return this.inputAmount;
        }
        else {
            const slippageAdjustedAmountIn = new math_1.Fraction(math_1.ONE)
                .add(slippageTolerance)
                .multiply(this.inputAmount.quotient).quotient;
            return currency_1.Amount.fromRawAmount(this.inputAmount.currency, slippageAdjustedAmountIn);
        }
    }
    /**
     * Return the execution price after accounting for slippage tolerance
     * @param slippageTolerance the allowed tolerated slippage
     */
    worstExecutionPrice(slippageTolerance) {
        return new currency_1.Price(this.inputAmount.currency, this.outputAmount.currency, this.maximumAmountIn(slippageTolerance).quotient, this.minimumAmountOut(slippageTolerance).quotient);
    }
    isV1() {
        return this.tradeVersion === Version_1.Version.V1;
    }
    isV2() {
        return this.tradeVersion === Version_1.Version.V2;
    }
    routeType() {
        if (this.isComplex())
            return 'Complex Path';
        if (this.isSingle())
            return 'Single Path';
        if (this.isSinglePool())
            return 'Single Pool';
        return 'Not Found';
    }
    isNotFound() {
        return !this.route.legs.length;
    }
    isComplex() {
        return new Set(this.route.legs.map((leg) => leg.tokenFrom.address)).size !== this.route.legs.length;
    }
    isSinglePool() {
        return this.route.legs.length === 1;
    }
    isSingle() {
        return new Set(this.route.legs.map((leg) => leg.tokenFrom.address)).size === this.route.legs.length;
    }
}
exports.Trade = Trade;
//# sourceMappingURL=Trade.js.map